<template>
  <div id="ticketsPage">
    <app-navbar></app-navbar>
    <div class="hero">
      <div class="container hero-content">
        <p class="eyebrow">{{ "Tickets.eyebrow" | staticTranslation }}</p>
        <h1>{{ "Tickets.title" | staticTranslation }}</h1>
        <p class="intro">
          {{ "Tickets.subtitle" | staticTranslation }}
          <a class="program-link" href="/program">{{ "Tickets.programLink" | staticTranslation }}</a>.
        </p>
      </div>
    </div>
    <div class="container content">
      <div v-if="tickets.length === 0" class="empty-state">
        {{ "Tickets.empty" | staticTranslation }}
      </div>
      <div v-else class="ticket-list">
        <div v-for="ticket in tickets" :key="ticket.id" class="ticket-card">
          <div class="ticket-row">
            <h2>{{ ticket.title }}</h2>
            <div class="ticket-details">
              <p class="place">{{ ticket.place }}</p>
              <div class="ticket-datetime">
                <p class="meta-value ticket-date">{{ ticket.dateLabel }}</p>
                <p class="meta-value ticket-time">{{ ticket.timeLabel }}</p>
              </div>
              <div class="ticket-action">
                <a
                    v-if="ticket.ticketLink"
                    :href="ticket.ticketLink"
                    class="ticket-button"
                    rel="noopener noreferrer"
                    target="_blank"
                >
                  {{ "Tickets.button" | staticTranslation }}
                </a>
                <span v-else class="ticket-button disabled">{{ "Tickets.button" | staticTranslation }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <app-footer></app-footer>
  </div>
</template>

<script>
import axios from "@/api/axiom-instance";
import footer from "@/components/base/footer";
import navbar from "@/components/base/navbar";

export default {
  name: "TicketsPage",
  components: {
    'app-navbar': navbar,
    'app-footer': footer
  },
  data() {
    return {
      tickets: []
    }
  },
  created() {
    axios.get("/ticket/visible").then((success) => {
      this.tickets = success.data
    }).catch(() => {
      this.tickets = []
    })
  }
}
</script>

<style scoped>
#ticketsPage {
  min-height: 100vh;
  background-color: #f15d22;
  color: #152326;
  overflow-x: hidden;
}

.hero {
  padding: 8.4rem 0 1.8rem;
}

.hero-content {
  max-width: 66rem;
  text-align: center;
}

.eyebrow {
  display: none;
}

h1 {
  margin: 0 0 0.7rem;
  font-size: 2.6rem;
  line-height: 0.96;
  letter-spacing: 0;
  font-family: "Transcript Pro Mono Regular";
  font-weight: 700;
}

.intro {
  max-width: 42rem;
  margin: 0 auto;
  font-size: 0.83rem;
  line-height: 1.35;
  font-family: "Transcript Mono";
}

.program-link {
  color: #152326;
  text-decoration: underline;
  font-family: "Transcript Pro Mono Regular";
  font-weight: 700;
}

.content {
  max-width: 66rem;
  padding-bottom: 4.9rem;
}

@media screen and (min-width: 576px) {
  .hero {
    padding-bottom: 1rem;
  }

  h1 {
    margin-bottom: 1.65rem;
  }

  .intro {
    font-size: 0.95rem;
    margin-bottom: 0;
  }

  .program-link {
    font-weight: 800;
  }

  .content {
    padding-top: 1.65rem;
  }
}

.ticket-list {
  width: 100%;
  max-width: 66rem;
  margin: 0 auto;
  background-color: #e4decf;
}

.ticket-card {
  position: relative;
  padding: 0.9rem 1.5rem 0.82rem;
  background-color: #e4decf;
  border: none;
}

.ticket-card::after {
  content: "";
  position: absolute;
  left: 1.5rem;
  right: 1.5rem;
  bottom: 0;
  height: 1px;
  background-color: rgba(241, 93, 34, 0.48);
}

.ticket-card:last-child::after {
  display: none;
}

.ticket-row {
  display: grid;
  row-gap: 0.34rem;
}

h2 {
  margin: 0;
  font-size: 1.45rem;
  line-height: 1.02;
  color: #152326;
  font-family: "Transcript Pro Mono Regular";
  font-weight: 700;
}

.ticket-details {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  column-gap: 1.2rem;
  align-items: center;
}

.ticket-datetime {
  display: flex;
  align-items: baseline;
  gap: 2.4rem;
  white-space: nowrap;
}

.place {
  margin: 0;
  color: #152326;
  font-family: "Transcript Mono";
  font-size: 0.98rem;
  line-height: 1.15;
}

.meta-value {
  color: #152326;
  font-family: "Transcript Mono";
  font-size: 0.98rem;
  font-weight: 400;
  line-height: 1.15;
  margin: 0;
  white-space: nowrap;
  text-align: left;
}

.ticket-action {
  display: flex;
  justify-content: flex-end;
  margin-left: 8rem;
}

.ticket-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 6.2rem;
  min-height: 1.9rem;
  padding: 0.32rem 0.8rem;
  border: none;
  background-color: #f2c45d;
  color: #152326;
  font-size: 0.82rem;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  font-family: "Transcript Pro Mono Regular";
  font-weight: 800;
}

.ticket-button:hover {
  color: #152326;
  background-color: #d8a93f;
  text-decoration: none;
}

.ticket-button.disabled {
  cursor: default;
  opacity: 0.65;
}

.empty-state {
  padding: 3rem 2rem;
  max-width: 66rem;
  margin: 0 auto;
  background-color: #e4decf;
  color: #152326;
  text-align: center;
  font-family: "Transcript Mono";
}

@media screen and (max-width: 991px) {
  h1 {
    font-size: 2.1rem;
  }

  .content,
  .ticket-list,
  .empty-state {
    max-width: 54rem;
  }

  .ticket-card {
    padding: 0.82rem 1.2rem 0.74rem;
  }

  .ticket-card::after {
    left: 1.2rem;
    right: 1.2rem;
  }

  .ticket-details {
    grid-template-columns: minmax(0, 1fr) auto auto;
    column-gap: 1rem;
  }

  .ticket-datetime {
    gap: 1.8rem;
  }

  .ticket-action {
    margin-left: 5.6rem;
  }

  h2 {
    font-size: 1.25rem;
  }

  .place,
  .meta-value {
    font-size: 0.88rem;
  }

  .ticket-button {
    min-width: 5.5rem;
    min-height: 1.72rem;
    font-size: 0.76rem;
  }
}

@media screen and (max-width: 575px) {
  .hero {
    padding-top: 6.35rem;
    padding-bottom: 0.96rem;
  }

  .hero .container,
  .content.container {
    padding-left: 0;
    padding-right: 0;
  }

  h1 {
    font-size: 1.86rem;
    text-align: center;
  }

  .hero-content {
    width: auto;
    max-width: none;
    margin: 0 0.95rem;
    text-align: center;
  }

  .intro {
    margin: 0 auto;
    max-width: none;
    font-size: 0.78rem;
    line-height: 1.35;
  }

  .content {
    width: auto;
    max-width: none;
    margin: 0 0.95rem;
    padding-top: 0;
    padding-bottom: 3.3rem;
  }

  .ticket-list {
    width: 100%;
    max-width: none;
  }

  .ticket-card {
    padding: 0.78rem 0.9rem 0.72rem;
  }

  h2 {
    font-size: 1.15rem;
    line-height: 1.04;
  }

  .ticket-details {
    grid-template-columns: 1fr;
    row-gap: 0.26rem;
  }

  .ticket-datetime {
    display: flex;
    align-items: baseline;
    gap: 0.65rem;
  }

  .place {
    margin-top: 0;
    font-size: 0.87rem;
  }

  .ticket-date,
  .ticket-time,
  .ticket-action {
    justify-self: start;
  }

  .ticket-date,
  .ticket-time {
    font-size: 0.87rem;
  }

  .ticket-time {
    margin-top: 0;
  }

  .ticket-action {
    display: block;
    width: auto;
    justify-self: start;
    margin-left: 0;
    margin-top: 0.12rem;
  }

  .ticket-button {
    min-width: 6.5rem;
    min-height: 1.58rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.24rem 0;
    font-size: 0.81rem;
    text-align: center;
  }

  .ticket-card::after {
    left: 0.9rem;
    right: 0.9rem;
  }
}

@media screen and (min-width: 1400px) {
  .hero {
    padding-top: 9rem;
  }

  .hero-content,
  .content,
  .ticket-list,
  .empty-state {
    max-width: 72rem;
  }
}
</style>
